import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../../firebase';
import { useToast } from './ToastContext';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useToast();
  const AUTH_OP_TIMEOUT_MS = 15000;
  const withTimeout = (promise, timeoutMs = AUTH_OP_TIMEOUT_MS) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error('auth-timeout')), timeoutMs))
    ]);

  // Maps raw Firebase error codes to user-friendly messages
  const getFirebaseErrorMessage = (error) => {
    switch (error.code) {
      // --- Credential / Login Errors ---
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/invalid-password':
        return 'Incorrect email or password. Please try again.';
      case 'auth/user-not-found':
        return 'No account found with this email. Please sign up first.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      // --- Sign Up Errors ---
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Try signing in instead.';
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters.';
      case 'auth/operation-not-allowed':
        return 'This sign-in method is not enabled. Please contact support.';
      // --- Network / Rate Errors ---
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please wait a moment and try again.';
      // --- Social / OAuth Errors ---
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method.';
      case 'auth/credential-already-in-use':
        return 'This account is already linked to another user.';
      case 'auth/cancelled-popup-request':
        return 'Sign in process was cancelled.';
      case 'auth/popup-closed-by-user':
      case 'auth/popup-blocked-by-browser':
        return 'Sign in was cancelled or blocked by the browser.';
      // --- Token / Session Errors ---
      case 'auth/expired-action-code':
        return 'This link has expired. Please request a new one.';
      case 'auth/invalid-action-code':
        return 'This link is invalid or has already been used.';
      case 'auth/requires-recent-login':
        return 'For security, please sign in again before continuing.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  // Sign up with email and password
  const signup = async (email, password) => {
    try {
      const result = await withTimeout(createUserWithEmailAndPassword(auth, email, password));
      setCurrentUser(result.user);
      showSuccess('Account created successfully!');
      return { success: true, user: result.user };
    } catch (error) {
      if (error?.message === 'auth-timeout' && auth.currentUser) {
        setCurrentUser(auth.currentUser);
        showSuccess('Account created successfully!');
        return { success: true, user: auth.currentUser };
      }
      const message = getFirebaseErrorMessage(error);
      return { success: false, error: message };
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      const result = await withTimeout(signInWithEmailAndPassword(auth, email, password));
      setCurrentUser(result.user);
      showSuccess('Welcome back!');
      return { success: true, user: result.user };
    } catch (error) {
      if (error?.message === 'auth-timeout' && auth.currentUser) {
        setCurrentUser(auth.currentUser);
        showSuccess('Welcome back!');
        return { success: true, user: auth.currentUser };
      }
      const message = getFirebaseErrorMessage(error);
      return { success: false, error: message };
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await withTimeout(signInWithPopup(auth, provider));
      setCurrentUser(result.user);
      if (showSuccess) showSuccess(`Welcome, ${result.user.displayName || 'User'}!`);
      return { success: true, user: result.user };
    } catch (error) {
      if (error?.message === 'auth-timeout' && auth.currentUser) {
        setCurrentUser(auth.currentUser);
        if (showSuccess) showSuccess(`Welcome, ${auth.currentUser.displayName || 'User'}!`);
        return { success: true, user: auth.currentUser };
      }
      const message = getFirebaseErrorMessage(error);
      return { success: false, error: message };
    }
  };

  // Sign in with Facebook
  const loginWithFacebook = async () => {
    try {
      const provider = new FacebookAuthProvider();
      const result = await withTimeout(signInWithPopup(auth, provider));
      setCurrentUser(result.user);
      if (showSuccess) showSuccess(`Welcome, ${result.user.displayName || 'User'}!`);
      return { success: true, user: result.user };
    } catch (error) {
      if (error?.message === 'auth-timeout' && auth.currentUser) {
        setCurrentUser(auth.currentUser);
        if (showSuccess) showSuccess(`Welcome, ${auth.currentUser.displayName || 'User'}!`);
        return { success: true, user: auth.currentUser };
      }
      const message = getFirebaseErrorMessage(error);
      return { success: false, error: message };
    }
  };

  // Sign in with Apple
  const loginWithApple = async () => {
    try {
      const provider = new OAuthProvider('apple.com');
      const result = await withTimeout(signInWithPopup(auth, provider));
      setCurrentUser(result.user);
      if (showSuccess) showSuccess(`Welcome, ${result.user.displayName || 'User'}!`);
      return { success: true, user: result.user };
    } catch (error) {
      if (error?.message === 'auth-timeout' && auth.currentUser) {
        setCurrentUser(auth.currentUser);
        if (showSuccess) showSuccess(`Welcome, ${auth.currentUser.displayName || 'User'}!`);
        return { success: true, user: auth.currentUser };
      }
      const message = getFirebaseErrorMessage(error);
      return { success: false, error: message };
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await withTimeout(signOut(auth));
      setCurrentUser(null);
      showSuccess('You have been signed out');
      return { success: true };
    } catch (error) {
      if (error?.message === 'auth-timeout' && !auth.currentUser) {
        setCurrentUser(null);
        showSuccess('You have been signed out');
        return { success: true };
      }
      showError('Failed to sign out. Please try again.');
      return { success: false, error: 'Failed to sign out. Please try again.' };
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = React.useMemo(() => ({
    currentUser,
    signup,
    login,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    logout,
    resetPassword,
    loading
  }), [currentUser, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
